// src/js/core/voiceConfig.js
import { getAccentPreference, setAccentPreference } from "./preferencesManager.js";

export const voiceConfig = {
  french: {
    label: "French",
    accents: {
      parisian: {
        label: "Parisian",
        locale: "fr-FR",
        voices: {
          male: {
            id: "aQROLel5sQbj1vuIVi6B",
          },
          female: {
            id: "dYjOkSQBPiH2igolJfeH",
          },
        },
      },
    },
  },
  spanish: {
    label: "Spanish",
    accents: {
      castilian: {
        label: "Spain accent",
        locale: "es-ES",
        voices: {
          male: {
            id: "Nh2zY9kknu6z4pZy6FhD",
          },
          female: {
            id: "KHCvMklQZZo0O30ERnVn",
          },
        },
      },
      mexican: {
        label: "Mexican accent",
        locale: "es-MX",
        voices: {
          male: {
            id: "gbTn1bmCvNgk0QEAVyfM",
          },
          female: {
            id: "2Lb1en5ujrODDIqmp7F3",
          },
        },
      },
    },
  },
  russian: {
    label: "Russian",
    accents: {
      standard: {
        label: "Standard",
        locale: "ru-RU",
        voices: {
          male: {
            id: "3EuKHIEZbSzrHGNmdYsx",
          },
          female: {
            id: "AB9XsbSA4eLG12t2myjN",
          },
        },
      },
    },
  },
  chinese: {
    label: "Chinese",
    accents: {
      standard: {
        label: "Standard",
        locale: "zh-CN",
        voices: {
          male: {
            id: "4VZIsMPtgggwNg7OXbPY",
          },
          female: {
            id: "hkfHEbBvdQFNX4uWHqRF",
          },
        },
      },
    },
  },
  korean: {
    label: "Korean",
    accents: {
      standard: {
        label: "Standard",
        locale: "ko-KR",
        voices: {
          male: {
            id: "1W00IGEmNmwmsDeYy7ag",
          },
          female: {
            id: "uyVNoMrnUku1dZyVEXwD",
          },
        },
      },
    },
  },
  japanese: {
    label: "Japanese",
    accents: {
      standard: {
        label: "Standard",
        locale: "ja-JP",
        voices: {
          male: {
            id: "3JDquces8E8bkmvbh6Bc",
          },
          female: {
            id: "8EkOjt4xTPGMclNlh1pk",
          },
        },
      },
    },
  },
  italian: {
    label: "Italian",
    accents: {
      standard: {
        label: "Standard",
        locale: "it-IT",
        voices: {
          male: {
            id: "13Cuh3NuYvWOVQtLbRN8",
          },
          female: {
            id: "uV2Bhcm1HwmAqPqkbjfl",
          },
        },
      },
    },
  },
  german: {
    label: "German",
    accents: {
      standard: {
        label: "Standard",
        locale: "de-DE",
        voices: {
          male: {
            id: "aTTiK3YzK3dXETpuDE2h",
          },
          female: {
            id: "dCnu06FiOZma2KVNUoPZ",
          },
        },
      },
    },
  },
  portuguese: {
    label: "Portuguese",
    accents: {
      european: {
        label: "European",
        locale: "pt-PT",
        voices: {
          male: {
            id: "aLFUti4k8YKvtQGXv0UO",
          },
          female: {
            id: "NdHRjGnnDKGnnm2c19le", // Note: This is actually a male voice because ElevenLabs did not have any female european portuguese voices as of 2025-05-07
          },
        },
      },
      brazilian: {
        label: "Brazilian",
        locale: "pt-BR",
        voices: {
          male: {
            id: "CstacWqMhJQlnfLPxRG4",
          },
          female: {
            id: "33B4UnXyTNbgLmdEDh5P",
          },
        },
      },
    },
  },
};

function getDefaultAccent(language) {
  const languageConfig = voiceConfig[language];
  return Object.keys(languageConfig?.accents || {})[0] || null;
}

// New function to validate if an accent exists for a language
function isValidAccent(language, accent) {
  return voiceConfig[language]?.accents?.[accent] !== undefined;
}

function getLocale(language, accent) {
  if (!isValidAccent(language, accent)) {
    accent = getDefaultAccent(language);
  }
  return voiceConfig[language]?.accents[accent]?.locale || null;
}

function getVoiceId(language, accent, gender) {
  // First validate if the accent exists for this language
  if (!isValidAccent(language, accent)) {
    console.warn(`Invalid accent "${accent}" for language "${language}". Falling back to default.`);
    accent = getDefaultAccent(language);
    // Update the stored preference to the valid accent
    setAccentPreference(language, accent);
  }
  return voiceConfig[language]?.accents[accent]?.voices[gender]?.id || null;
}

export function updateVoiceSelect(selectedLanguage) {
  let accentSelect = document.getElementById("accent-select");

  if (!accentSelect) {
    accentSelect = document.createElement("select");
    accentSelect.id = "accent-select";
    accentSelect.classList.add("hide-when-limited");
    accentSelect.style.display = "none";

    const topControls = document.querySelector(".topControls-dropdowns");
    topControls.insertAdjacentElement("afterend", accentSelect);

    accentSelect.addEventListener("change", (e) => {
      const newAccent = e.target.value;
      const currentLanguage = document.getElementById("language-select").value;

      // Save preference
      setAccentPreference(currentLanguage, newAccent);

      // Verify it was saved correctly
      const savedAccent = getAccentPreference(currentLanguage);
      if (savedAccent !== newAccent) {
        // If preference wasn't saved properly, try again
        setAccentPreference(currentLanguage, newAccent);
      }
    });
  }

  accentSelect.innerHTML = "";
  const languageAccents = voiceConfig[selectedLanguage]?.accents || {};

  // Get saved preference or default
  let savedAccent = getAccentPreference(selectedLanguage);

  // Validate the saved accent is valid for this language
  if (!isValidAccent(selectedLanguage, savedAccent)) {
    savedAccent = getDefaultAccent(selectedLanguage);
    setAccentPreference(selectedLanguage, savedAccent);
  }

  Object.entries(languageAccents).forEach(([accentKey, accent]) => {
    const option = document.createElement("option");
    option.value = accentKey;
    option.textContent = accent.label;
    option.selected = accentKey === savedAccent;
    accentSelect.appendChild(option);
  });

  updateVoiceSelectVisibility();
}

export function updateVoiceSelectVisibility() {
  const accentSelect = document.getElementById("accent-select");
  if (!accentSelect) return;

  const selectedLanguage = document.getElementById("language-select").value;
  const languageAccents = voiceConfig[selectedLanguage]?.accents || {};
  const hasContent = document.getElementById("output").innerHTML.trim() !== "";

  accentSelect.style.display = Object.keys(languageAccents).length > 1 && hasContent ? "block" : "none";
}

export { getVoiceId, getDefaultAccent, getLocale };
